import React, { useState } from 'react';

/**
 * NotificationPreferences Component
 *
 * Comprehensive notification management interface for provider settings
 * Allows providers to control which notifications they receive
 *
 * Categories:
 * - Client billing
 * - Client management
 * - Documentation
 * - Insurance
 * - Messaging
 * - Scheduling
 * - Tasks
 * - Emails (individual settings)
 */

interface NotificationToggle {
  id: string;
  label: string;
  enabled: boolean;
  description?: string;
}

interface NotificationCategory {
  id: string;
  title: string;
  expanded: boolean;
  notifications: NotificationToggle[];
}

const NotificationPreferences: React.FC = () => {
  const [categories, setCategories] = useState<NotificationCategory[]>([
    {
      id: 'client-billing',
      title: 'Client billing',
      expanded: false,
      notifications: [
        { id: 'outstanding-balances', label: 'Outstanding client balances', enabled: true },
        { id: 'uninvoiced-amounts', label: 'Uninvoiced amounts', enabled: true }
      ]
    },
    {
      id: 'client-management',
      title: 'Client management',
      expanded: false,
      notifications: [
        { id: 'possible-couples', label: 'Possible couple clients', enabled: true },
        { id: 'duplicate-clients', label: 'Possible duplicate clients or contacts', enabled: true },
        { id: 'upcoming-birthdays', label: 'Upcoming birthdays', enabled: true }
      ]
    },
    {
      id: 'documentation',
      title: 'Documentation',
      expanded: false,
      notifications: [
        { id: 'doc-requests-completed', label: 'Document requests completed by clients', enabled: true },
        { id: 'good-faith-expiring', label: 'Good faith estimates expiring', enabled: true },
        { id: 'missing-diagnosis', label: 'Missing diagnosis and treatment plans', enabled: true },
        { id: 'missing-mental-status', label: 'Missing mental status exams', enabled: true },
        { id: 'missing-progress-notes', label: 'Missing progress notes', enabled: true },
        { id: 'pending-doc-requests', label: 'Pending document requests for clients', enabled: true },
        { id: 'unreviewed-diagnosis', label: 'Unreviewed diagnosis and treatment plans', enabled: true }
      ]
    },
    {
      id: 'insurance',
      title: 'Insurance',
      expanded: false,
      notifications: [
        { id: 'expiring-authorizations', label: 'Clients with expiring insurance authorizations', enabled: true },
        { id: 'insurance-payments', label: 'Insurance payments received', enabled: true },
        { id: 'unallocated-payments', label: 'Insurance payments with unallocated funds', enabled: true }
      ]
    },
    {
      id: 'messaging',
      title: 'Messaging',
      expanded: false,
      notifications: [
        { id: 'contact-form-inquiries', label: 'Contact form inquiries submitted by prospective clients', enabled: true },
        { id: 'email-unsubscribed', label: 'Clients unsubscribed to email reminders', enabled: true },
        { id: 'text-unsubscribed', label: 'Clients unsubscribed to text reminders', enabled: true }
      ]
    },
    {
      id: 'scheduling',
      title: 'Scheduling',
      expanded: false,
      notifications: [
        { id: 'existing-client-requests', label: 'Existing clients submit appointment requests', enabled: true },
        { id: 'prospective-client-requests', label: 'Prospective clients submit appointment requests', enabled: true }
      ]
    },
    {
      id: 'tasks',
      title: 'Tasks',
      expanded: false,
      notifications: [
        { id: 'overdue-tasks', label: 'Overdue tasks', enabled: true }
      ]
    }
  ]);

  const [emailSettings, setEmailSettings] = useState([
    {
      id: 'daily-agenda',
      title: 'Daily agenda',
      description: 'An email summary sent each morning that includes upcoming appointments, appointment requests, reminders, and billing updates.',
      enabled: true
    },
    {
      id: 'evening-summary',
      title: 'Evening summary',
      description: 'An email summary sent each evening about the appointments and AutoPay amounts that have occurred in the day.',
      enabled: true
    },
    {
      id: 'secure-messages',
      title: 'Secure messages',
      description: 'An email sent in addition to the in app notification you receive for new secure messages.',
      enabled: true
    }
  ]);

  const toggleCategory = (categoryId: string) => {
    setCategories(prev => prev.map(cat =>
      cat.id === categoryId ? { ...cat, expanded: !cat.expanded } : cat
    ));
  };

  const toggleNotification = (categoryId: string, notificationId: string) => {
    setCategories(prev => prev.map(cat => {
      if (cat.id === categoryId) {
        return {
          ...cat,
          notifications: cat.notifications.map(notif =>
            notif.id === notificationId ? { ...notif, enabled: !notif.enabled } : notif
          )
        };
      }
      return cat;
    }));
  };

  const toggleEmailSetting = (settingId: string) => {
    setEmailSettings(prev => prev.map(setting =>
      setting.id === settingId ? { ...setting, enabled: !setting.enabled } : setting
    ));
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1200px', fontFamily: 'Arial, sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{
          fontSize: '32px',
          fontWeight: '400',
          color: '#F7E9A0',
          margin: '0 0 8px 0'
        }}>
          Notification preferences
        </h1>
        <p style={{
          fontSize: '16px',
          color: '#e5e7eb',
          margin: 0
        }}>
          Manage the notifications you receive
        </p>
      </div>

      {/* Notifications Section */}
      <div style={{ marginBottom: '48px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#e5e7eb',
            margin: 0
          }}>
            Notifications
          </h2>
          <span style={{
            fontSize: '13px',
            color: '#d1d5db',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            🏢 PRACTICE SETTINGS
          </span>
        </div>

        <p style={{
          fontSize: '14px',
          color: '#d1d5db',
          marginBottom: '24px'
        }}>
          <strong style={{ color: '#e5e7eb' }}>Note:</strong> Not all are listed—some are essential and can't be turned off.
        </p>

        {/* Notification Categories */}
        {categories.map(category => (
          <div key={category.id} style={{
            borderBottom: '1px solid #e5e7eb',
            marginBottom: '0'
          }}>
            <button
              onClick={() => toggleCategory(category.id)}
              style={{
                width: '100%',
                padding: '20px 0',
                background: 'none',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                color: '#e5e7eb',
                textAlign: 'left'
              }}
            >
              <span style={{
                transform: category.expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.2s',
                fontSize: '12px',
                color: '#d1d5db'
              }}>
                ▼
              </span>
              {category.title}
            </button>

            {category.expanded && (
              <div style={{ paddingLeft: '32px', paddingBottom: '20px' }}>
                {category.notifications.map(notification => (
                  <div key={notification.id} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      cursor: 'pointer',
                      flex: 1
                    }}>
                      <div
                        onClick={() => toggleNotification(category.id, notification.id)}
                        style={{
                          width: '44px',
                          height: '24px',
                          borderRadius: '12px',
                          backgroundColor: notification.enabled ? '#0066cc' : '#4b5563',
                          position: 'relative',
                          transition: 'background-color 0.2s',
                          cursor: 'pointer',
                          flexShrink: 0
                        }}
                      >
                        <div style={{
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          position: 'absolute',
                          top: '2px',
                          left: notification.enabled ? '22px' : '2px',
                          transition: 'left 0.2s'
                        }} />
                      </div>
                      <span style={{
                        fontSize: '15px',
                        color: '#d1d5db'
                      }}>
                        {notification.label}
                      </span>
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Emails Section */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#e5e7eb',
            margin: 0
          }}>
            Emails
          </h2>
          <span style={{
            fontSize: '13px',
            color: '#d1d5db',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            👤 INDIVIDUAL SETTINGS
          </span>
        </div>

        {/* Email Settings */}
        {emailSettings.map((setting, index) => (
          <div key={setting.id} style={{
            paddingBottom: '24px',
            marginBottom: index < emailSettings.length - 1 ? '24px' : '0',
            borderBottom: index < emailSettings.length - 1 ? '1px solid #4b5563' : 'none'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px'
            }}>
              <div
                onClick={() => toggleEmailSetting(setting.id)}
                style={{
                  width: '44px',
                  height: '24px',
                  borderRadius: '12px',
                  backgroundColor: setting.enabled ? '#0066cc' : '#4b5563',
                  position: 'relative',
                  transition: 'background-color 0.2s',
                  cursor: 'pointer',
                  flexShrink: 0,
                  marginTop: '2px'
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  position: 'absolute',
                  top: '2px',
                  left: setting.enabled ? '22px' : '2px',
                  transition: 'left 0.2s'
                }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  color: '#e5e7eb',
                  marginBottom: '4px'
                }}>
                  {setting.title}
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#d1d5db',
                  lineHeight: '1.5'
                }}>
                  {setting.description}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NotificationPreferences;
